import type {
  CreatePlatformUserInput,
  PlatformUser,
  UpdatePlatformUserInput,
} from '../../shared/models';

const USERS_STORAGE_KEY = 'habita4u_platform_users';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function assertUniqueEmail(users: PlatformUser[], email: string, ignoredUserId?: string) {
  const normalizedEmail = normalizeEmail(email);
  const emailExists = users.some((user) => (
    user.id !== ignoredUserId &&
    normalizeEmail(user.email) === normalizedEmail
  ));

  if (emailExists) {
    throw new Error('Já existe um usuário cadastrado com este e-mail.');
  }
}

function parseStoredUser(value: unknown): PlatformUser | null {
  if (!isRecord(value)) return null;

  const id = typeof value.id === 'string' ? value.id : '';
  const name = typeof value.name === 'string' ? value.name : '';
  const email = typeof value.email === 'string' ? value.email : '';
  const profile = value.profile;
  const status = value.status;

  if (
    !id ||
    !name ||
    !email ||
    !['SDR', 'GERENTE', 'SUPERVISOR', 'ADMIN'].includes(String(profile)) ||
    !['ACTIVE', 'INACTIVE'].includes(String(status))
  ) {
    return null;
  }

  return {
    id,
    name,
    email,
    profile: profile as PlatformUser['profile'],
    status: status as PlatformUser['status'],
    created_at: typeof value.created_at === 'string' ? value.created_at : '',
    updated_at: typeof value.updated_at === 'string' ? value.updated_at : '',
  };
}

function sortUsers(users: PlatformUser[]): PlatformUser[] {
  return [...users].sort((left, right) => (
    left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' }) ||
    left.email.localeCompare(right.email, 'pt-BR', { sensitivity: 'base' })
  ));
}

export class UsersRepository {
  async fetchUsers(): Promise<PlatformUser[]> {
    if (typeof window === 'undefined') return [];

    try {
      const parsed = JSON.parse(window.localStorage.getItem(USERS_STORAGE_KEY) ?? '[]');
      const users = Array.isArray(parsed)
        ? parsed.map(parseStoredUser).filter((user): user is PlatformUser => Boolean(user))
        : [];

      return sortUsers(users);
    } catch {
      return [];
    }
  }

  async createUser(input: CreatePlatformUserInput): Promise<PlatformUser> {
    const users = await this.fetchUsers();
    assertUniqueEmail(users, input.email);

    const now = new Date().toISOString();
    const user: PlatformUser = {
      id: createId(),
      name: input.name.trim(),
      email: normalizeEmail(input.email),
      profile: input.profile,
      status: 'ACTIVE',
      created_at: now,
      updated_at: now,
    };

    this.saveUsers([...users, user]);
    return user;
  }

  async updateUser(id: string, input: UpdatePlatformUserInput): Promise<PlatformUser> {
    const users = await this.fetchUsers();
    const currentUser = users.find((user) => user.id === id);
    if (!currentUser) {
      throw new Error('Usuário não encontrado.');
    }

    assertUniqueEmail(users, input.email, id);

    const updatedUser: PlatformUser = {
      ...currentUser,
      name: input.name.trim(),
      email: normalizeEmail(input.email),
      profile: input.profile,
      status: input.status,
      updated_at: new Date().toISOString(),
    };

    this.saveUsers(users.map((user) => user.id === id ? updatedUser : user));
    return updatedUser;
  }

  private saveUsers(users: PlatformUser[]) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(sortUsers(users)));
  }
}
